import React, { useState } from 'react';
import { Loader2, Heart, Sparkles, Rocket, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import SEO from '../components/SEO';

const Careers = () => {
  const [form, setForm] = useState({ name: '', email: '', resume: '', interest: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.submitInquiry({
        type: 'careers',
        name: form.name,
        email: form.email,
        message: `Resume: ${form.resume}\n\nWhy interested in Bronto Platforms:\n${form.interest}`,
      });
      toast.success('Application received. Our team will be in touch.');
      setForm({ name: '', email: '', resume: '', interest: '' });
    } catch {
      toast.error('Unable to submit at this time. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#050505] text-white" data-testid="careers-page">
      <SEO
        title="Careers — Build the Future with Bronto Platforms"
        description="Join Bronto Platforms Private Limited. We're building artificial intelligence, agricultural intelligence, and space discovery platforms. Apply to work on technology that matters."
        path="/careers"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Careers
          </div>
          <h1 className="text-display text-5xl md:text-7xl lg:text-8xl mb-10 max-w-5xl">
            Work on <span className="rainbow-text">technology</span> that matters.
          </h1>
          <p className="text-xl md:text-2xl text-white/65 max-w-4xl leading-relaxed font-light">
            Join Bronto Platforms in building artificial intelligence platforms, agricultural intelligence systems, and future digital technologies that improve how people interact with information and markets.
          </p>
        </div>
      </section>

      {/* Who we look for */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
            Who We Look For
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 max-w-3xl">
            Passionate, hardworking people.
          </h2>
          <p className="text-lg text-white/65 leading-relaxed max-w-3xl mb-10">
            We are building a long-term technology organization. We look for people who bring genuine passion, a strong work ethic, and the curiosity to keep learning. We actively support youth — talented young engineers, designers, and thinkers ready to take on ambitious problems.
          </p>

          <div className="rounded-2xl glass-strong p-6 md:p-8 max-w-3xl mb-16 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500" />
            <div className="relative z-10">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
                At Bronto Platforms, we believe
              </div>
              <p className="text-xl md:text-2xl font-medium tracking-tight leading-snug">
                &ldquo;The more you know, the more <span className="rainbow-text">magical</span> it becomes.&rdquo;
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/5 rounded-3xl overflow-hidden">
            <div className="bg-[#050505] p-8 md:p-10">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-5">
                <Heart className="h-5 w-5 text-white/70" />
              </div>
              <h3 className="text-xl font-bold tracking-tight mb-3">Passion</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                We value people who care deeply about the work and the impact it creates.
              </p>
            </div>
            <div className="bg-[#050505] p-8 md:p-10">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-5">
                <Rocket className="h-5 w-5 text-white/70" />
              </div>
              <h3 className="text-xl font-bold tracking-tight mb-3">Hard Work</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                Building meaningful technology requires sustained effort, discipline, and ownership.
              </p>
            </div>
            <div className="bg-[#050505] p-8 md:p-10">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-5">
                <Sparkles className="h-5 w-5 text-white/70" />
              </div>
              <h3 className="text-xl font-bold tracking-tight mb-3">We Support Youth</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                We back ambitious young talent and provide a platform to do their most important work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
            Apply
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Reach out to us.
          </h2>
          <p className="text-base md:text-lg text-white/60 leading-relaxed mb-3 max-w-2xl">
            Share your resume and tell us why you want to be part of Bronto Platforms. We review every application personally.
          </p>
          <p className="text-sm text-white/45 mb-12">
            Applications are routed to <span className="text-white/70 font-medium">hr@brontointelligence.com</span>.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="careers-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                  Full Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="Your name"
                  data-testid="careers-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="you@example.com"
                  data-testid="careers-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                Resume Link
              </label>
              <input
                type="url"
                value={form.resume}
                onChange={(e) => setForm({ ...form, resume: e.target.value })}
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="https://... (Google Drive, LinkedIn, Dropbox)"
                data-testid="careers-resume"
              />
              <p className="text-[11px] text-white/35 mt-2">
                Paste a shareable link to your resume. Make sure it&apos;s set to &quot;anyone with the link can view.&quot;
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                Why are you interested in Bronto Platforms?
              </label>
              <textarea
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
                required
                rows={6}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="Tell us about yourself and why you want to join us..."
                data-testid="careers-interest"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="careers-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <Send className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Careers;
